// Registry de renderers para el resultado on-screen de las Plantillas SmartPay (engine=LEGACY).
// GENERAL, PAYMENT_METHOD, DISCOUNTS, REFUNDS y TIPS comparten literalmente el mismo controller
// legacy (getOrdersByBranchOffice) y por lo tanto la misma forma de respuesta
// ({orders, analitics, temp_orders}) - por eso comparten un unico renderer (OrdersAnalyticsRenderer)
// en vez de una tabla 100% generica o 9 renderers a medida.
const money = (n) => new Intl.NumberFormat("es-PA", { style: "currency", currency: "USD" }).format(Number(n) || 0);
const dt = (v) => v ? new Date(v).toLocaleString("es-PA") : "";

function SimpleTable({ columns, rows }) {
  if (!rows || rows.length === 0) return <div className="rb-legacy-empty">Sin datos para este filtro.</div>;
  return (
    <div className="rb-table-wrap">
      <table className="rb-table">
        <thead><tr>{columns.map(c => <th key={c.key}>{c.label}</th>)}</tr></thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={row.id || row._id || row.order_id || i}>
              {columns.map(c => <td key={c.key}>{c.format ? c.format(row[c.key]) : (row[c.key] ?? "")}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SummaryRow({ items }) {
  const visible = items.filter(([, value]) => value !== undefined && value !== null);
  if (!visible.length) return null;
  return (
    <div className="rb-legacy-summary">
      {visible.map(([label, value]) => (
        <div key={label} className="rb-legacy-summary__item"><span>{label}</span><b>{value}</b></div>
      ))}
    </div>
  );
}

const CardCashIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="6" width="20" height="12" rx="2" /><circle cx="12" cy="12" r="2.5" /><path d="M6 6v0M18 18v0" />
  </svg>
);

// Tarjetas por metodo de pago (analitics.paymenthods: {name, total, number_transactions}),
// mismo dato que ya usa el tab legacy PaymentMethodReports.jsx para sus cards.
function PaymentMethodCards({ paymenthods }) {
  if (!paymenthods || paymenthods.length === 0) return null;
  return (
    <div className="rb-legacy-paymethod-grid">
      {paymenthods.map((pm, i) => (
        <div key={pm.paymentmethod_id ?? pm.name ?? i} className="rb-legacy-paymethod-card">
          <div className="rb-legacy-paymethod-card__value">{money(pm.total)}</div>
          <div className="rb-legacy-paymethod-card__body">
            <span className="rb-legacy-paymethod-card__name">{pm.name}</span>
            {pm.number_transactions !== undefined && (
              <span className="rb-legacy-paymethod-card__count">Número de transacciones: {pm.number_transactions}</span>
            )}
          </div>
          <div className="rb-legacy-paymethod-card__icon"><CardCashIcon /></div>
        </div>
      ))}
    </div>
  );
}

// GENERAL, PAYMENT_METHOD, DISCOUNTS, REFUNDS, TIPS -> respuesta de getOrdersByBranchOffice
export function OrdersAnalyticsRenderer({ payload }) {
  const analitics = payload?.analitics || {};
  const orders = payload?.orders || [];
  const columns = [
    { key: "transaction_code", label: "Orden" },
    { key: "createdAt", label: "Fecha", format: dt },
    { key: "order_type", label: "Tipo" },
    { key: "total", label: "Total", format: money },
    { key: "tax", label: "Impuesto", format: money },
    { key: "discount", label: "Descuento", format: money },
  ];
  return (
    <div className="rb-legacy-result">
      <SummaryRow items={[
        ["Total ventas", money(analitics.totalSales)],
        ["Subtotal", money(analitics.subTotalSales)],
        ["Impuestos", money(analitics.totalTaxSales)],
        ["Descuentos", money(analitics.totalDiscounts)],
        ["Devoluciones", money(analitics.totalDevolutions)],
        analitics.totalTips !== undefined ? ["Propinas", money(analitics.totalTips)] : undefined,
      ].filter(Boolean)} />
      <SimpleTable columns={columns} rows={orders} />
    </div>
  );
}

// PAYMENT_METHOD -> misma respuesta que OrdersAnalyticsRenderer (getOrdersByBranchOffice),
// pero ademas antepone las cards por metodo de pago (analitics.paymenthods) que ya muestra
// el tab legacy PaymentMethodReports.jsx, en vez de solo el resumen generico de totales.
export function PaymentMethodRenderer({ payload }) {
  const analitics = payload?.analitics || {};
  const orders = payload?.orders || [];
  const columns = [
    { key: "transaction_code", label: "Orden" },
    { key: "createdAt", label: "Fecha", format: dt },
    { key: "order_type", label: "Tipo" },
    { key: "total", label: "Total", format: money },
    { key: "tax", label: "Impuesto", format: money },
    { key: "discount", label: "Descuento", format: money },
  ];
  return (
    <div className="rb-legacy-result">
      <PaymentMethodCards paymenthods={analitics.paymenthods} />
      <SummaryRow items={[
        ["Subtotal", money(analitics.subTotalSales)],
        ["Impuestos", money(analitics.totalTaxSales)],
        ["Total de ventas", money(analitics.totalSales)],
        ["Devoluciones", money(analitics.totalDevolutions)],
        ["Descuentos", money(analitics.totalDiscounts)],
      ]} />
      <SimpleTable columns={columns} rows={orders} />
    </div>
  );
}

// PRODUCTS -> respuesta de getProductSales: {total_product_sales, total_combos_sales}
export function ProductSalesRenderer({ payload }) {
  const products = payload?.total_product_sales || [];
  const combos = payload?.total_combos_sales || [];
  // Nombres de columna reales de getProductTotalSales/getCombosTotalSales (services/reports.js):
  // group_name/combo_name, total_amount_selled (unidades), total_selled (monto).
  const productColumns = [
    { key: "group_name", label: "Producto" },
    { key: "total_amount_selled", label: "Cantidad" },
    { key: "total_selled", label: "Total", format: money },
  ];
  const comboColumns = [
    { key: "combo_name", label: "Combo" },
    { key: "total_amount_selled", label: "Cantidad" },
    { key: "total_selled", label: "Total", format: money },
  ];
  return (
    <div className="rb-legacy-result">
      <div className="rb-legacy-result__section-title">Productos</div>
      <SimpleTable columns={productColumns} rows={products} />
      {combos.length > 0 && <>
        <div className="rb-legacy-result__section-title">Combos</div>
        <SimpleTable columns={comboColumns} rows={combos} />
      </>}
    </div>
  );
}

// PRE_ACCOUNT -> respuesta de getDeletedTemporalOrders: {analitics:{branchofficeDate_orders}, temp_orders}
export function PreAccountRenderer({ payload }) {
  const analitics = payload?.analitics || {};
  const groups = analitics.branchofficeDate_orders || [];
  const columns = [
    { key: "transaction_code", label: "Orden" },
    { key: "deletedAt", label: "Eliminada", format: dt },
    { key: "total", label: "Total", format: money },
    { key: "deleted_code", label: "Motivo" },
  ];
  return (
    <div className="rb-legacy-result">
      <SummaryRow items={[
        ["Total", money(analitics.totalSales)],
        ["Subtotal", money(analitics.subTotalSales)],
        ["Impuestos", money(analitics.totalTaxSales)],
        ["Descuentos", money(analitics.totalDiscounts)],
        ["Devoluciones", money(analitics.totalDevolutions)],
      ]} />
      {groups.length === 0 && <div className="rb-legacy-empty">Sin pre-cuentas eliminadas para este filtro.</div>}
      {groups.map((group, i) => (
        <div key={i} style={{ marginBottom: 16 }}>
          <div className="rb-legacy-result__section-title">{group.branchOfficeDate}</div>
          <SimpleTable columns={columns} rows={group.orders || []} />
        </div>
      ))}
    </div>
  );
}

// BUYS_AND_SALES -> {buys_sales}; CLOSURES -> {informsz} - ambos arrays planos, columnas fijas simples.
export function SimpleListRenderer({ payload, reportCode }) {
  const isClosures = reportCode === "CLOSURES";
  const rows = isClosures ? (payload?.informsz || []) : (payload?.buys_sales || []);
  const columns = isClosures
    ? [
      { key: "code", label: "Cierre" },
      { key: "user", label: "Usuario", format: (v) => v?.name || "" },
      { key: "branchoffice", label: "Sucursal", format: (v) => v?.name || "" },
      { key: "createdAt", label: "Fecha", format: dt },
    ]
    // Nombres reales de columna de getSaleBuyHistoryByBranch (services/buys.js): la fecha viene
    // como buy_sale_createdAt y la cantidad como buy_sale_amount (no createdAt/amount a secas).
    : [
      { key: "buy_sale_createdAt", label: "Fecha", format: dt },
      { key: "group_name", label: "Producto" },
      { key: "is_sale", label: "Venta", format: (v) => v ? "Si" : "No" },
      { key: "is_buy", label: "Compra", format: (v) => v ? "Si" : "No" },
      { key: "buy_sale_amount", label: "Cantidad" },
      { key: "order_id", label: "Orden" },
    ];
  return <div className="rb-legacy-result"><SimpleTable columns={columns} rows={rows} /></div>;
}

export const REPORT_RENDERERS = {
  GENERAL: OrdersAnalyticsRenderer,
  PAYMENT_METHOD: PaymentMethodRenderer,
  DISCOUNTS: OrdersAnalyticsRenderer,
  REFUNDS: OrdersAnalyticsRenderer,
  TIPS: OrdersAnalyticsRenderer,
  PRODUCTS: ProductSalesRenderer,
  PRE_ACCOUNT: PreAccountRenderer,
  BUYS_AND_SALES: SimpleListRenderer,
  CLOSURES: SimpleListRenderer,
};

export function RenderLegacyResult({ reportCode, payload }) {
  const Renderer = REPORT_RENDERERS[reportCode];
  if (!Renderer) return <div className="rb-legacy-empty">Reporte no soportado.</div>;
  return <Renderer payload={payload} reportCode={reportCode} />;
}
